import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    useTheme,
    CircularProgress,
    useMediaQuery,
    Alert
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import logo from '../assets/img/logo.png';
import { useNavigate } from 'react-router-dom';

const EntryPass = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const nameRef = useRef(null);
    const membershipTypeRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [rotation, setRotation] = useState(0);
    const [entryPassData, setEntryPassData] = useState(null);

    // Animate logo rotation
    const animate = useCallback(() => {
        let time = 0;
        const animationFrame = () => {
            time += 0.01;
            const angle = Math.sin(time) * 60;
            setRotation(angle);
            return requestAnimationFrame(animationFrame);
        };
        return animationFrame();
    }, []);

    useEffect(() => {
        const animationFrameId = animate();
        return () => cancelAnimationFrame(animationFrameId);
    }, [animate]);

    // Check entry pass validity
    useEffect(() => {
        const checkEntryPass = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.get('/users/entry-pass');
                setEntryPassData(response.data);
            } catch (error) {
                setError(error.response?.data?.message || 'Unable to verify entry pass');
                navigate('/dashboard');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            checkEntryPass();
        } else {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Update time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Scale name and membership type to fit container
    useEffect(() => {
        const updateScale = () => {
            if (nameRef.current) {
                const container = nameRef.current.parentElement;
                const containerWidth = container.offsetWidth - 18; // Subtract left and right padding (24px each)
                const scale = Math.min(1, containerWidth / nameRef.current.offsetWidth);
                nameRef.current.style.setProperty('--scale', scale);
            }
            if (membershipTypeRef.current) {
                const container = membershipTypeRef.current.parentElement;
                const scale = Math.min(1, container.offsetWidth / membershipTypeRef.current.offsetWidth);
                membershipTypeRef.current.style.setProperty('--scale', scale);
            }
        };

        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, [entryPassData]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!entryPassData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#4CAF50',
                color: 'white',
                overflow: 'hidden',
                position: 'fixed',
                top: 0,
                left: 0,
                padding: isMobile ? '16px' : '32px'
            }}
        >
            {/* Rotating Logo */}
            <Box
                sx={{
                    position: 'absolute',
                    top: isMobile ? '15%' : '15%',
                    transform: `rotate(${rotation}deg)`,
                    width: isMobile ? '180px' : '180px',
                    height: 'auto',
                    zIndex: 1
                }}
            >
                <img 
                    src={logo} 
                    alt="TTE Logo"
                    style={{
                        width: '100%',
                        height: 'auto',
                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.3))'
                    }}
                />
            </Box>

            {/* Main Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    width: '100%',
                    mt: isMobile ? '120px' : '220px',
                    mb: isMobile ? 0 : '200px',
                    px: 2
                }}
            >
                {/* Name Container */}
                <Box
                    sx={{
                        width: '90%',
                        maxWidth: '90vw',
                        overflow: 'hidden',
                        mb: isMobile ? 2 : 3,
                        display: 'flex',
                        justifyContent: 'center',
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        borderRadius: '12px',
                        padding: '12px 24px'
                    }}
                >
                    <Typography 
                        variant="h3"
                        sx={{ 
                            fontSize: isMobile ? '10vw' : '8vw',
                            fontWeight: 'bold',
                            textShadow: '4px 4px 8px rgba(0,0,0,0.4)',
                            textAlign: 'center',
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            transform: 'scale(var(--scale))',
                            transformOrigin: 'center center',
                            letterSpacing: '-0.02em'
                        }}
                        ref={nameRef}
                    >
                        {entryPassData.firstName} {entryPassData.lastName}
                    </Typography>
                </Box>

                {/* Membership Type */}
                <Box
                    sx={{
                        width: '90%',
                        maxWidth: '90vw',
                        overflow: 'hidden',
                        mb: isMobile ? 3 : 4,
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Typography 
                        variant="h3"
                        sx={{ 
                            fontSize: isMobile ? '7vw' : '4vw',
                            fontWeight: 'medium',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                            textAlign: 'center',
                            opacity: 0.9,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            transform: 'scale(var(--scale))',
                            transformOrigin: 'center center'
                        }}
                        ref={membershipTypeRef}
                    >
                        {entryPassData.membershipType}
                    </Typography>
                </Box>
            </Box>

            {/* Time Display */}
            <Box
                sx={{
                    width: '100%',
                    textAlign: 'center',
                    position: 'fixed',
                    bottom: isMobile ? '40px' : '60px',
                    left: 0,
                    right: 0,
                    zIndex: 2
                }}
            >
                <Typography 
                    variant="h3"
                    sx={{ 
                        fontSize: isMobile ? '6vw' : '3.5rem',
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        opacity: 0.8,
                        letterSpacing: '2px',
                        mb: 1
                    }}
                >
                    {currentTime.toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                    })}
                </Typography>
                <Typography 
                    variant="h3"
                    sx={{ 
                        fontSize: isMobile ? '6vw' : '3.5rem',
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        opacity: 0.8,
                        letterSpacing: '2px'
                    }}
                >
                    {currentTime.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true 
                    })}
                </Typography>
            </Box>
        </Box>
    );
};

export default EntryPass; 